// hooks/usePusher.js
import { useEffect, useRef } from "react";
import Pusher from "pusher-js";
import apiEndpoints from "../apiconfig";

export default function usePusher() {
  const pusherRef = useRef(null);
  const channelRef = useRef(null);

  useEffect(() => {
    //console.log('[usePusher] 🔄 Hook mounted');

    const token = sessionStorage.getItem("token");
    if (!token) {
      //console.log('[usePusher] ❌ No authentication token found, skipping Pusher initialization');
      return;
    }

    const init = async () => {
      try {
        //console.log('[usePusher] ⏳ Fetching user GUID...');

        const response = await fetch(apiEndpoints.getGuid, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        // console.log('[usePusher] 📡 GUID fetch response status:', response.status);
        if (!response.ok) throw new Error("Failed to fetch user GUID");

        const data = await response.json();
        // console.log('[usePusher] 📥 GUID response data:', data);

        const userGuid = data.guid;
        if (!userGuid) {
          console.error("[usePusher] ❌ No GUID found in response");
          return;
        }
        // console.log("[usePusher] ✅ User GUID:", userGuid);

        // Initialize Pusher
        // console.log("[usePusher] 🚀 Initializing Pusher client...");
        pusherRef.current = new Pusher("ea8941fae9b57f3fbff9", {
          cluster: "ap2",
          forceTLS: true,
        });

        // Connection logs
        pusherRef.current.connection.bind("connected", () => {
          // console.log("[usePusher] 🔌 Pusher connected successfully");
        });

        pusherRef.current.connection.bind("disconnected", () => {
          console.warn("[usePusher] 🔌 Pusher disconnected");
        });

        pusherRef.current.connection.bind("error", (err) => {
          console.error("[usePusher] ⚠️ Pusher connection error:", err);
        });

        // Subscribe to channel
        // console.log(`[usePusher] 📡 Subscribing to channel: user-${userGuid}`);
        channelRef.current = pusherRef.current.subscribe(`user-${userGuid}`);

        channelRef.current.bind("pusher:subscription_succeeded", () => {
          // console.log(
          //   `[usePusher] ✅ Successfully subscribed to user-${userGuid}`
          // );
        });

        channelRef.current.bind("pusher:subscription_error", (status) => {
          console.error("[usePusher] ❌ Subscription error:", status);
        });

        // Capture ALL events
        channelRef.current.bind_global((event, payload) => {
          // console.log(
          // `[usePusher] 🌐 Global event received: ${event}`,
          // payload
          // );
        });

        // Handle new-notification
        channelRef.current.bind("new-notification", (payload) => {
          //  console.log(
          //  "[usePusher] 🔔 Event received: new-notification",
          //  payload
          // );

          if (payload?.message) {
            if ("Notification" in window) {
              if (Notification.permission === "granted") {
                // console.log("[usePusher] ✅ Showing native notification");
                new Notification("New Notification", {
                  body: payload.message,
                  icon: "/icon-192x192.png",
                });
              } else if (Notification.permission === "default") {
                // console.log(
                //   "[usePusher] 📩 Requesting notification permission..."
                // );
                Notification.requestPermission().then((perm) => {
                  if (perm === "granted") {
                    // console.log(
                    //   "[usePusher] ✅ Permission granted, showing notification"
                    // );
                    new Notification("New Notification", {
                      body: payload.message,
                      icon: "/icon-192x192.png",
                    });
                  } else {
                    console.warn(
                      "[usePusher] ❌ Notification permission denied"
                    );
                  }
                });
              }
            }

            // Dispatch custom event
            //  console.log(
            //   "[usePusher] 📤 Dispatching custom window event: new-pusher-notification"
            // );
            window.dispatchEvent(
              new CustomEvent("new-pusher-notification", { detail: payload })
            );
          }
        });
      } catch (err) {
        console.error("[usePusher] ❌ Error initializing Pusher:", err);
      }
    };

    init();

    // Cleanup
    return () => {
      if (pusherRef.current) {
        // console.log("[usePusher] 🧹 Cleaning up, disconnecting Pusher");
        pusherRef.current.disconnect();
      }
    };
  }, []);
}
